/**
 * The `electron` global, for a browser that has no Electron behind it.
 *
 * `src/preload.ts` puts this object on `window` through `contextBridge` in the desktop app,
 * and 18 files under `src/ui` reach for it — for the clipboard, for microphone permission, for
 * window controls, and for a couple of dozen fire-and-forget IPC messages to the companion
 * window. Rewriting those to ask "am I in a browser?" one by one would put the question in 18
 * places and leave the 19th to be found in production.
 *
 * So the bridge is reimplemented instead of removed, and the app above it does not change.
 * Three kinds of answer live here:
 *
 *   1. Things the browser does natively, done natively — the clipboard, microphone permission,
 *      the language the app starts in, and saving a file.
 *   2. Things that mean nothing without a desktop — the companion window, the auto-updater,
 *      window controls, the native menu. These accept the call and do nothing, which is what
 *      the callers already tolerate: every one of them is fire-and-forget or wrapped in a
 *      try/catch with a working fallback.
 *   3. Things that would be actively wrong to fake — OS-backed encryption. `secureStorage`
 *      reports itself unavailable, exactly as it does on a Linux desktop with no keyring, and
 *      `AuthenticationStore` already falls back to plain storage when it does.
 *
 * On (3) it is worth being explicit about what the browser build is trusting: nothing. No token
 * is stored by the page — the access token lives in memory and is gone on reload — and the
 * refresh and device tokens never reach JavaScript at all. The API sends them as HttpOnly cookies
 * when it sees `X-Dictaris-Client: web`, which is the header `@dictaris/core` defines and the
 * reason it exists — see the API's `BrowserSessionCookies`. `AuthenticationStore` therefore
 * persists nothing in this build, and `restoreBrowserSession` rebuilds the session on load.
 *
 * This module must be imported before anything that touches `electron`, which in practice
 * means before `src/i18n/renderer` — it calls `initialLanguage()` while being imported, so
 * that the first frame is painted in the right language. `src/web/index.tsx` is what guarantees
 * that order.
 */

import { SUPPORTED_LANGUAGES, FALLBACK_LANGUAGE, isSupportedLanguage } from '@dictaris/i18n';

// Type-only, and it has to stay that way: `src/preload` imports Electron itself. It is here for
// the contract check at the bottom of this file.
import type * as Preload from 'src/preload';

import { ClipboardChannels, type ClipboardPayload } from 'src/channels/clipboardChannels';
import {
  ExportChannels,
  type ExportPdfPayload,
  type ExportResult,
  type SaveFilePayload,
} from 'src/channels/exportChannels';
import { HotKeyChannels } from 'src/channels/hotKeyChannels';
import { LanguageChannels } from 'src/channels/languageChannels';
import type { MicrophonePermissionState } from 'src/channels/mediaPermissionChannels';
import { log } from 'src/log';

/** Where the chosen language is kept. The desktop app's main process owns the equivalent file. */
const LANGUAGE_KEY = 'dictaris-web-language';

// Language ------------------------------------------------------------------

/**
 * The language to start in: the user's stored choice, or the closest supported match for what
 * the browser reports, or Romanian.
 *
 * `navigator.languages` is ordered by preference and carries regions ("de-AT"), so each entry
 * is reduced to its primary subtag before being matched.
 */
function initialLanguage(): string {
  try {
    const stored = window.localStorage.getItem(LANGUAGE_KEY);
    if (isSupportedLanguage(stored)) return stored;
  } catch (error) {
    // Safari in private mode throws on localStorage rather than returning null.
    log.warn('Could not read the stored language', error);
  }

  const preferred = navigator.languages?.length ? navigator.languages : [navigator.language];

  for (const tag of preferred) {
    const primary = tag?.split('-')[0]?.toLowerCase();
    if ((SUPPORTED_LANGUAGES as readonly string[]).includes(primary)) return primary;
  }

  return FALLBACK_LANGUAGE;
}

function rememberLanguage(language: unknown): void {
  if (!isSupportedLanguage(language)) return;

  try {
    window.localStorage.setItem(LANGUAGE_KEY, language);
  } catch (error) {
    log.warn('Could not store the chosen language', error);
  }
}

// Saving a file -------------------------------------------------------------

/**
 * The browser's answer to a save dialog: hand the file to the download manager.
 *
 * There is no file path to report back and no way to learn whether the user kept the file, so
 * this reports `saved: true` on the narrower claim it can actually make — the download started.
 * That is the claim the notification in `DictationEditorExporter` is worth showing for.
 */
function saveFile(payload: SaveFilePayload): ExportResult {
  const { data, fileName, extension } = payload;

  // `data` crosses the desktop bridge as a Uint8Array and arrives here as one; Blob wants its
  // own copy of the buffer either way.
  const blob = new Blob([new Uint8Array(data)], { type: mimeTypeFor(extension) });
  const url = URL.createObjectURL(blob);

  try {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${fileName}.${extension}`;
    anchor.rel = 'noopener';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();

    return { saved: true };
  } finally {
    // Revoked on the next tick rather than immediately: some browsers have not finished
    // reading the blob when click() returns, and revoking under them cancels the download.
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }
}

function mimeTypeFor(extension: string): string {
  switch (extension) {
    case 'docx':
      return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    case 'pdf':
      return 'application/pdf';
    case 'rtf':
      return 'application/rtf';
    default:
      return 'application/octet-stream';
  }
}

/**
 * PDF, by way of the browser's own print-to-PDF.
 *
 * The desktop app renders the same HTML in an offscreen Chromium window and writes a file.
 * A browser cannot do that without shipping a PDF engine in the bundle, and it does not need
 * to: every browser this app supports can print a page to PDF, and the HTML the exporter
 * already produces is written for exactly that — an A4 `@page`, inline images as data URIs,
 * and break rules.
 *
 * A hidden iframe rather than a new window, because a popup blocker silently swallows the
 * second one and there is no reliable way to tell that it did.
 *
 * It reports `saved: false`: the print dialog does not say what the user chose, and a
 * notification claiming the report was exported when they pressed Cancel is worse than no
 * notification. `runExport` treats a false with no error as a cancelled dialog and stays quiet.
 */
function printToPdf(payload: ExportPdfPayload): Promise<ExportResult> {
  const { html, fileName, landscape } = payload;

  return new Promise<ExportResult>((resolve) => {
    const frame = document.createElement('iframe');
    frame.setAttribute('aria-hidden', 'true');
    frame.style.position = 'fixed';
    frame.style.right = '0';
    frame.style.bottom = '0';
    frame.style.width = '0';
    frame.style.height = '0';
    frame.style.border = '0';

    const cleanUp = () => {
      window.setTimeout(() => frame.remove(), 1_000);
    };

    let printed = false;

    frame.onload = () => {
      const frameWindow = frame.contentWindow;

      // Only the report itself, and only once. An iframe is given an empty about:blank
      // document before its srcdoc, and Chromium fires `load` for that one synchronously,
      // inside appendChild. With the content set after insertion, as it used to be, the first
      // print dialog opened on that empty page — the blank PDF — and a second one followed with
      // the report in it.
      if (printed || (frameWindow && frameWindow.location.href !== 'about:srcdoc')) return;
      printed = true;

      try {
        if (!frameWindow) throw new Error('The print frame has no window');

        // The browser names the file after the document title, so this is what turns
        // "about:blank.pdf" into the report's own name.
        frameWindow.document.title = fileName;
        frameWindow.focus();
        frameWindow.print();

        resolve({ saved: false });
      } catch (error) {
        log.error('Could not open the print dialog', error);
        resolve({ saved: false, error: String(error) });
      } finally {
        cleanUp();
      }
    };

    // Content before insertion, so the only document this frame ever loads is the report.
    const orientation = landscape ? '<style>@page { size: A4 landscape; }</style>' : '';
    frame.srcdoc = orientation ? html.replace('</head>', `${orientation}</head>`) : html;

    document.body.appendChild(frame);
  });
}

// Clipboard -----------------------------------------------------------------

/**
 * Rich copy, as far as the browser allows it.
 *
 * `ClipboardItem` carries the HTML flavour alongside the plain-text one, which is what makes a
 * paste into Word keep its headings and lists. RTF is not a flavour any browser will write, so
 * the payload's `rtf` is dropped; Word prefers `text/html` over RTF anyway when both are
 * offered, so nothing is lost that was reaching Word.
 */
async function writeClipboardObject(payload: ClipboardPayload): Promise<boolean> {
  const { text, html } = payload;

  try {
    if (html && typeof ClipboardItem !== 'undefined') {
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/html': new Blob([html], { type: 'text/html' }),
          'text/plain': new Blob([text ?? ''], { type: 'text/plain' }),
        }),
      ]);
      return true;
    }

    await navigator.clipboard.writeText(text ?? '');
    return true;
  } catch (error) {
    // Firefox refuses `clipboard.write` outside a user gesture and has no rich-text path at
    // all. Returning false rather than throwing puts the caller on its plain-text fallback,
    // which it already has.
    log.warn('Could not write the rich clipboard payload', error);
    return false;
  }
}

async function writeClipboardText(content: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(content);
    return true;
  } catch (error) {
    log.warn('Could not write to the clipboard', error);
    return false;
  }
}

async function readClipboardText(): Promise<string> {
  try {
    return await navigator.clipboard.readText();
  } catch (error) {
    // Reading needs a permission most browsers only grant on an explicit gesture, and Firefox
    // never grants it to a page. An empty string is what the callers treat as "nothing there".
    log.warn('Could not read the clipboard', error);
    return '';
  }
}

// Microphone permission -----------------------------------------------------

/**
 * The browser's microphone permission, in the shape the desktop app's main process reports.
 *
 * `canOpenSettings` is false and stays false: no browser exposes an API to open its own site
 * permissions, and `MicrophoneTester` already renders instructions instead of a button when it
 * is. `platform` is 'other' for the same reason it is on Linux — there is no per-application
 * OS switch here, the permission belongs to the origin.
 */
async function microphoneState(): Promise<MicrophonePermissionState> {
  const base = { canOpenSettings: false, platform: 'other' as const };

  // Chromium-only. Firefox implements the Permissions API but not the 'microphone' name, and
  // throws a TypeError rather than resolving.
  try {
    const status = await navigator.permissions?.query({ name: 'microphone' as PermissionName });

    if (status?.state === 'granted') return { ...base, status: 'granted', canAskOs: false };
    if (status?.state === 'denied') return { ...base, status: 'denied', canAskOs: false };
    if (status?.state === 'prompt') return { ...base, status: 'not-determined', canAskOs: true };
  } catch (error) {
    log.warn('This browser does not report the microphone permission', error);
  }

  // Not knowing must not stand in the way of a recording: `useMicrophonePermission` treats
  // 'unknown' as "try anyway", and getUserMedia will prompt when the moment comes.
  return { ...base, status: 'unknown', canAskOs: true };
}

/**
 * Asks for access the only way a browser offers: open the microphone, then close it again.
 *
 * Opening the device is what raises the prompt, and the track is stopped immediately so the
 * recording indicator does not stay lit while the user is still on the settings screen.
 */
async function requestMicrophoneAccess(): Promise<MicrophonePermissionState> {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    stream.getTracks().forEach((track) => track.stop());
  } catch (error) {
    log.warn('Microphone access was refused', error);
    return { status: 'denied', canOpenSettings: false, canAskOs: false, platform: 'other' };
  }

  return microphoneState();
}

// The bridge ----------------------------------------------------------------

// The same listener signature the desktop bridge publishes, so the two cannot drift.
type Listener = Preload.RendererListener;

/**
 * Registered listeners, kept so that `on` and the unsubscribe it returns behave like a real
 * emitter.
 *
 * Only the hotkeys are ever emitted on — see {@link onHotKeyDown}. Every other channel the
 * renderer listens to — window state, menu events, deep links, the auto-updater — is sent by a
 * main process that does not exist here. They are honoured rather than ignored so that
 * `useRendererListener`'s cleanup works.
 */
const listeners = new Map<string, Set<Listener>>();

function emit(channel: string, ...args: unknown[]): void {
  // No IpcRendererEvent exists here, and no listener on these channels reads its first argument.
  const event = undefined as unknown as Parameters<Listener>[0];
  listeners.get(channel)?.forEach((listener) => listener(event, ...args));
}

/**
 * F1 and F3, taken from the page and delivered the way the desktop's global shortcuts deliver
 * them — as HOTKEY_EVENT on the bridge — so `DictationRecorder` handles both hosts with one
 * listener and does not know which it is on.
 *
 * Only while the tab has focus: a page cannot claim keys machine-wide, so the desktop's "works
 * over another program's patient record" does not carry over. Default actions are cancelled
 * because both keys already mean something to the browser (help, find next), and a doctor
 * pressing F1 to dictate should not also get a help tab.
 *
 * Unmodified presses only, matching the desktop accelerators — Shift+F3 stays find-previous.
 * A held key's auto-repeat is dropped, which is what `hotkeyRepeatGuard` does on the desktop:
 * a held F1 would otherwise start, stop and restart the dictation for as long as it was down.
 */
function onHotKeyDown(event: KeyboardEvent): void {
  if (event.key !== HotKeyChannels.F1 && event.key !== HotKeyChannels.F3) return;
  if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;

  event.preventDefault();
  if (event.repeat) return;

  emit(HotKeyChannels.HOTKEY_EVENT, event.key);
}

async function invoke(channel: string, ...args: unknown[]): Promise<unknown> {
  switch (channel) {
    case ExportChannels.SAVE_FILE:
      return saveFile(args[0] as SaveFilePayload);

    case ExportChannels.EXPORT_PDF:
      return printToPdf(args[0] as ExportPdfPayload);

    case ClipboardChannels.WRITE_OBJECT:
      return writeClipboardObject(args[0] as ClipboardPayload);

    case ClipboardChannels.WRITE_TEXT:
    case ClipboardChannels.WRITE_HTML:
    case ClipboardChannels.WRITE_RTF:
      return writeClipboardText(String(args[0] ?? ''));

    case ClipboardChannels.READ_TEXT:
    case ClipboardChannels.READ_HTML:
    case ClipboardChannels.READ_RTF:
      return readClipboardText();

    default:
      // Companion window, window controls, the native menu, the auto-updater, pending
      // recordings on disk. Every caller of these either ignores the answer or has a
      // documented fallback for `undefined` — `PendingRecordingService` coalesces to null and
      // to an empty list, which is the truth here: a browser has no crash-recovery folder.
      return undefined;
  }
}

export const webElectronBridge = {
  /** Nothing here runs on Chromium-the-runtime or Node, so there are no versions to report. */
  versions: {} as Record<string, unknown>,

  /**
   * "Is this a release build?" — which is all the callers ask it. It gates the developer
   * panel and the problem-reports link, and must not be confused with which *environment* the
   * app talks to: that comes from `src/web/environment.ts`, because a production build serves
   * the test deployment too.
   */
  isPackaged: (): boolean => !import.meta.env.DEV,

  initialLanguage,

  ipcRenderer: {
    send(channel: string, ...args: unknown[]) {
      if (channel === LanguageChannels.LANGUAGE_CHANGED) rememberLanguage(args[0]);
      // Everything else is a message to a main process that is not there.
    },

    invoke,

    // Returning the unsubscribe, as the desktop bridge does — see `on` in src/preload.ts for
    // why that bridge has no `removeListener`.
    on(channel: string, listener: Listener): () => void {
      const existing = listeners.get(channel) ?? new Set<Listener>();
      existing.add(listener);
      listeners.set(channel, existing);
      return () => {
        listeners.get(channel)?.delete(listener);
      };
    },

    once(channel: string, listener: Listener): () => void {
      const unsubscribe = this.on(channel, (event, ...args) => {
        unsubscribe();
        listener(event, ...args);
      });
      return unsubscribe;
    },
  },

  clipboard: {
    writeText: writeClipboardText,
    readText: readClipboardText,
    writeRTF: writeClipboardText,
    readRTF: readClipboardText,
    writeHTML: writeClipboardText,
    readHTML: readClipboardText,
    writeObject: writeClipboardObject,
  },

  mediaPermissions: {
    getMicrophoneState: microphoneState,
    requestMicrophoneAccess,
    /** No browser lets a page open its own site settings. The UI shows instructions instead. */
    openMicrophoneSettings: async (): Promise<boolean> => false,
  },

  /**
   * No OS credential store to reach from a page.
   *
   * Reporting false rather than faking encryption is the point: it is honest about what a
   * browser can protect. Nothing relies on it any more — `AuthenticationStore` stores nothing in
   * this build, and the tokens worth protecting are HttpOnly cookies the API keeps.
   */
  secureStorage: {
    isAvailable: (): boolean => false,
    encrypt: (): string | null => null,
    decrypt: (): string | null => null,
  },
};

/** Puts the bridge where the app expects to find it. Idempotent, so a hot reload is harmless. */
export function installWebElectronBridge(): void {
  Object.defineProperty(globalThis, 'electron', {
    value: webElectronBridge,
    writable: false,
    configurable: true,
  });

  // Capture phase, so an editor that stops propagation cannot swallow the key. Removed first so
  // a hot reload does not stack a second listener and deliver every press twice.
  window.removeEventListener('keydown', onHotKeyDown, true);
  window.addEventListener('keydown', onHotKeyDown, true);
}

/**
 * Compile-time proof that this shim still answers everything the desktop bridge does.
 *
 * `src/preload.ts` is the definition of what `electron` is, and `src/@types/globals.d.ts`
 * declares the global as exactly its type — so the app is type-checked against the desktop
 * bridge whichever host it is built for. Without this line the shim could quietly fall behind:
 * a method added to the preload bridge would type-check everywhere, and fail at runtime in the
 * browser only, on whichever screen happened to call it.
 *
 * It is an assignment and not a cast on purpose. A cast would silence exactly the error this
 * exists to raise.
 */
const bridgeContract: typeof Preload.globals = webElectronBridge;
void bridgeContract;
